package com.snakesworld.game.domain

import com.snakesworld.game.engine.GameConstants
import com.snakesworld.game.engine.SnakeSegment
import com.snakesworld.game.engine.Vector2D
import kotlin.math.abs
import kotlin.math.floor
import kotlin.math.round

sealed interface HeadToHeadResult {
    data object NoCollision : HeadToHeadResult
    data object MutualDemise : HeadToHeadResult
}

/**
 * Geometric collision resolution for rectangular (axis-aligned grid-cell) snake
 * segments, head-to-head mutual demise, square arena bounds, and pellet pickup.
 *
 * Mass and length are score/growth counters only. They never change cell size and
 * never decide who survives a collision.
 */
object CollisionDetection {

    /**
     * Checks if the position is outside the square arena boundary.
     * The arena extends from [-arenaHalfSize, arenaHalfSize] along both X and Y axes.
     */
    fun isOutOfBounds(position: Vector2D, arenaHalfSize: Float = GameConstants.ARENA_HALF_SIZE): Boolean {
        return abs(position.x) > arenaHalfSize || abs(position.y) > arenaHalfSize
    }

    /**
     * Snaps a scalar onto the [spacing] grid (nearest cell center / vertex).
     */
    fun snapToGrid(value: Float, spacing: Float = GameConstants.SEGMENT_SPACING): Float {
        if (spacing <= 0f) return value
        return round(value / spacing) * spacing
    }

    /**
     * Snaps a world position onto the aligned [spacing] grid.
     */
    fun snapToGrid(position: Vector2D, spacing: Float = GameConstants.SEGMENT_SPACING): Vector2D {
        return Vector2D(snapToGrid(position.x, spacing), snapToGrid(position.y, spacing))
    }

    /**
     * Largest grid-center coordinate whose [GameConstants.CELL_HALF_SIZE] square
     * lies fully inside the arena. The next center (one [GameConstants.SEGMENT_SPACING]
     * farther) is already past the wall, which is where a snake steps when it dies
     * on the boundary and where a near-edge coordinate snaps outward.
     */
    fun maxPelletCenterInsideArena(
        arenaHalfSize: Float = GameConstants.ARENA_HALF_SIZE,
        cellHalf: Float = GameConstants.CELL_HALF_SIZE,
        spacing: Float = GameConstants.SEGMENT_SPACING,
    ): Float {
        val inset = arenaHalfSize - cellHalf
        if (inset <= 0f || spacing <= 0f) return 0f
        return floor(inset / spacing) * spacing
    }

    /**
     * Snaps [position] onto the pellet grid and pulls it in so the whole square
     * stays inside the arena. Safe to apply to ambient food, death orbs, and
     * multiplayer snapshots.
     */
    fun clampPelletInsideArena(
        position: Vector2D,
        arenaHalfSize: Float = GameConstants.ARENA_HALF_SIZE,
    ): Vector2D {
        val maxCenter = maxPelletCenterInsideArena(arenaHalfSize)
        val snapped = snapToGrid(position)
        return Vector2D(
            snapped.x.coerceIn(-maxCenter, maxCenter),
            snapped.y.coerceIn(-maxCenter, maxCenter),
        )
    }

    /** True when a pellet square centered at [position] does not cross the arena wall. */
    fun isPelletFullyInside(
        position: Vector2D,
        arenaHalfSize: Float = GameConstants.ARENA_HALF_SIZE,
        cellHalf: Float = GameConstants.CELL_HALF_SIZE,
    ): Boolean {
        return abs(position.x) + cellHalf <= arenaHalfSize && abs(position.y) + cellHalf <= arenaHalfSize
    }

    /**
     * True when two uniform [spacing]×[spacing] squares overlap (interior AABB).
     * Adjacent cells share an edge (`abs(dx) == spacing`) and do not count as a hit.
     */
    fun occupiesSameGridCell(
        a: Vector2D,
        b: Vector2D,
        spacing: Float = GameConstants.SEGMENT_SPACING,
    ): Boolean {
        return abs(a.x - b.x) < spacing && abs(a.y - b.y) < spacing
    }

    /**
     * Checks if the snake's head overlaps a trailing body square.
     * Skips the first [ignoreSegments] neck segment(s) — the cell the head just left —
     * so a 90° turn cannot false-kill. Occupying any older body cell (including a 2×2
     * loop onto the tail) is fatal. Adjacent grazing is not a bite.
     */
    fun checkSelfCollision(
        head: Vector2D,
        segments: List<SnakeSegment>,
        ignoreSegments: Int = GameConstants.SELF_COLLISION_IGNORE_SEGMENTS,
        spacing: Float = GameConstants.SEGMENT_SPACING,
    ): Boolean {
        if (segments.size <= ignoreSegments) return false
        for (i in ignoreSegments until segments.size) {
            if (occupiesSameGridCell(head, segments[i].position, spacing)) {
                return true
            }
        }
        return false
    }

    /**
     * Checks if the snake's head occupies the same grid cell as any opponent body segment.
     * Same rectangular cell test as self-collision: adjacent slither is not predation.
     * The snake whose head entered the occupied body cell dies; mass does not matter.
     */
    fun checkOpponentBodyCollision(
        head: Vector2D,
        opponentSegments: List<SnakeSegment>,
        spacing: Float = GameConstants.SEGMENT_SPACING,
    ): Boolean {
        for (seg in opponentSegments) {
            if (occupiesSameGridCell(head, seg.position, spacing)) {
                return true
            }
        }
        return false
    }

    /**
     * Resolves head-to-head occupancy: two heads in the same grid cell always perish
     * together. Mass is ignored.
     */
    fun checkHeadToHeadCollision(
        headA: Vector2D,
        headB: Vector2D,
        spacing: Float = GameConstants.SEGMENT_SPACING,
    ): HeadToHeadResult {
        if (!occupiesSameGridCell(headA, headB, spacing)) {
            return HeadToHeadResult.NoCollision
        }
        return HeadToHeadResult.MutualDemise
    }

    /**
     * Pellets occupy the same grid cells as snake parts. Pickup is same-cell overlap,
     * not a radius or mass-scaled hit box.
     */
    fun checkPelletPickup(
        head: Vector2D,
        pelletPosition: Vector2D,
        spacing: Float = GameConstants.SEGMENT_SPACING,
    ): Boolean = occupiesSameGridCell(head, pelletPosition, spacing)
}
